import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ViewTabs } from '@/components/ui/tabs'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridFilterChangeEvent,
  GridFooterCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  TradingAccountLotsProfitStatisticDto,
  TradingAccountLotsProfitStatisticDtoKey,
} from 'api/Trading-Accounts/Types/TradingAccountLotsProfitStatisticDto'
import { ReactDatePicker } from 'components/DatePicker/DatePicker'
import { InputFilterCell } from 'components/Filter'
import { Loading } from 'components/Loading'
import { format } from 'date-fns'
import { Search } from 'lucide-react'
import omitEmpty from 'omit-empty'
import { pageableSettings } from 'store/grid/grid.slice'
import styled from 'styled-components'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import { transactionLotsProfitColumns } from './transactionLotsProfitColumns'

interface Props {
  walletId?: string
  clientId?: string
}

enum Tab {
  CLIENTS = 'clients',
  WALLETS = 'wallets',
}

interface Values {
  from?: number | null
  to?: number | null
}

const Container = styled.div`
  align-items: center;
  display: flex;

  > label {
    font-weight: bold;
    margin-right: 3rem;
  }

  > span {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  select,
  input {
    margin: 0.5rem;
    color: #0e76eb;
    max-width: 200px;
    height: 32px;
    padding: 4px 11px;
    //color: rgba(0,0,0,.65);
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    -webkit-transition: all 0.3s;
    transition: all 0.3s;
    text-transform: capitalize;
  }
`
const ReactDatePickerContainer = styled.div`
  z-index: 2;
`

export const TransactionDetailsLotsProfitStatistics: React.FC<Props> = ({ clientId, walletId }) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [activeTab, setActiveTab] = useState<Tab>(Tab.CLIENTS)
  const [dateRanges, setDateRange] = useState<Values>({})
  const [gridSize, setGridSize] = useState<{ take: number; skip: number }>(
    getPaginationInitialState()
  )

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    async (query?: PageQuery) => {
      if (activeTab === Tab.WALLETS && walletId) {
        return tradingAccountsApiClient.getTradingAccountTradedLots(
          walletId,
          activeTab,
          omitEmpty({
            ...query,
            pageSize: gridSize.take,
            skip: gridSize.skip,
            dateFrom: dateRanges.from,
            dateTo: dateRanges.to,
          })
        )
      }
      if (activeTab === Tab.CLIENTS && clientId) {
        return tradingAccountsApiClient.getTradingAccountTradedLots(
          clientId,
          activeTab,
          omitEmpty({
            ...query,
            pageSize: gridSize.take,
            skip: gridSize.skip,
            dateFrom: dateRanges.from,
            dateTo: dateRanges.to,
          })
        )
      }
    },
    [clientId, walletId, activeTab, gridSize] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const { data = [], isLoading } = useFetchOne(callback)

  const setFromDate = (date: Date | null) => {
    if (date) {
      date.setHours(0, 0, 0, 0)
    }
    setDate(date, 'from')
  }

  const setToDate = (date: Date | null) => {
    if (date) {
      date.setHours(23, 59, 59, 999)
    }
    setDate(date, 'to')
  }

  const setDate = (date: Date | null, type: 'from' | 'to') => {
    if (date) {
      setDateRange({ ...dateRanges, [type]: format(date, "yyyy-MM-dd'T'HH:mm:ss") })
    } else {
      setDateRange({ ...dateRanges, [type]: undefined })
    }
  }

  const handleFilterChange = () => {
    forceRefresh()
  }

  return (
    <React.Fragment>
      <Container>
        <label>Period</label>
        <span>from</span>
        <ReactDatePickerContainer>
          <ReactDatePicker
            showYearDropdown
            dropdownMode='select'
            isClearable
            dateFormat='dd/MM/yyyy'
            selected={dateRanges.from ? new Date(dateRanges.from) : null}
            onChange={(date) => setFromDate(date)}
            customInput={<input style={{ width: '100%' }} type='text' autoComplete='off' />}
          />
        </ReactDatePickerContainer>
        <span>to</span>
        <ReactDatePickerContainer>
          <ReactDatePicker
            showYearDropdown
            dropdownMode='select'
            isClearable
            dateFormat='dd/MM/yyyy'
            selected={dateRanges.to ? new Date(dateRanges.to) : null}
            onChange={(date) => setToDate(date)}
            customInput={<input style={{ width: '100%' }} type='text' autoComplete='off' />}
          />
        </ReactDatePickerContainer>
        <span />
        <Button onClick={handleFilterChange} data-test='filter-search-button'>
          <Search className='size-4' />
          Search
        </Button>
      </Container>
      <ViewTabs
        defaultKey={activeTab}
        tabs={[
          {
            key: Tab.CLIENTS,
            translateKey: <span>Client Level</span>,
            visible: true,
            component: (
              <Table
                setGridSize={setGridSize}
                gridSize={gridSize}
                data={data}
                isLoading={isLoading}
              />
            ),
          },
          {
            key: Tab.WALLETS,
            translateKey: <span>Wallet Level</span>,
            visible: true,
            component: (
              <Table
                setGridSize={setGridSize}
                gridSize={gridSize}
                data={data}
                isLoading={isLoading}
              />
            ),
          },
        ]}
        onChange={(tabKey) => setActiveTab(tabKey as Tab)}
      />
    </React.Fragment>
  )
}

interface TableProps {
  data: TradingAccountLotsProfitStatisticDto[]
  isLoading: boolean
  setGridSize: React.Dispatch<
    React.SetStateAction<{
      take: number
      skip: number
    }>
  >
  gridSize: {
    take: number
    skip: number
  }
}

const initialFilter: CompositeFilterDescriptor = {
  filters: [],
  logic: 'and',
}

const Table: React.FC<TableProps> = ({ data, isLoading, setGridSize, gridSize }) => {
  const [filter, setFilter] = useState(initialFilter)

  const getFilteredData = (
    tradingAccountLotsProfitStatisticDtos: TradingAccountLotsProfitStatisticDto[]
  ) => filterBy(tradingAccountLotsProfitStatisticDtos, filter)

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const pageChange = async (event: GridPageChangeEvent) => {
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    setGridSize({ take: event.page.take, skip: event.page.skip })
  }

  const footerCell = (props: GridFooterCellProps) => {
    const cell = props.field as 'profit' | 'lots'
    const total = getFilteredData(data).reduce<number>((previousValue, currentValue) => {
      return previousValue + currentValue[cell]
    }, 0)

    return (
      <td colSpan={props.colSpan} style={props.style}>
        total: {total}
      </td>
    )
  }

  return (
    <Loading isLoading={isLoading}>
      <Grid
        filterable
        resizable
        data={getFilteredData(data)}
        take={gridSize.take}
        skip={gridSize.skip}
        total={getFilteredData(data).length}
        filter={filter}
        onPageChange={pageChange}
        onFilterChange={handleFilterChange}
        pageable={pageableSettings}
      >
        {transactionLotsProfitColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          title='Lots'
          field={TradingAccountLotsProfitStatisticDtoKey.LOTS}
          filter='numeric'
          className='table-columns__number'
          footerCell={footerCell}
        />
        <Column
          title='Profit'
          field={TradingAccountLotsProfitStatisticDtoKey.PROFIT}
          filter='numeric'
          className='table-columns__number'
          footerCell={footerCell}
        />
        <Column
          title='Currency'
          filter='text'
          filterCell={InputFilterCell}
          filterable
          field={TradingAccountLotsProfitStatisticDtoKey.CURRENCY}
        />
      </Grid>
    </Loading>
  )
}
